import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ForumAPI } from '../utils/api';
import { getToken } from '../utils/auth';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { FaBold, FaItalic, FaHeading, FaListUl, FaListOl, FaLink, FaImage, FaCode, FaQuoteRight } from 'react-icons/fa';

const CreateForumPostPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [categoryLabel, setCategoryLabel] = useState('');
    const [categories, setCategories] = useState<any[]>([]);
    const [imageUrl, setImageUrl] = useState('');
    const [previewBaseUrl, setPreviewBaseUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        import('../utils/api').then(({ getApiBaseUrl }) => {
            const apiUrl = getApiBaseUrl();
            const rootUrl = apiUrl.split('/api')[0].split('/index.php')[0];
            setPreviewBaseUrl(rootUrl);
        });

        const catParam = searchParams.get('category');
        if (catParam) {
            setCategoryName(catParam);
            import('../utils/api').then(({ ForumCategoriesAPI }) => {
                ForumCategoriesAPI.list().then(data => {
                    const found = data.categories.find((c: any) => c.name === catParam);
                    if (found) {
                        setCategoryLabel(found.label);
                    } else {
                        setCategoryLabel('Chuyên mục không xác định');
                    }
                });
            });
        } else {
            navigate('/forum');
            toast.error('Vui lòng chọn chuyên mục trước khi đăng bài');
        }
    }, [searchParams]);

    const insertMarkdown = (prefix: string, suffix: string = '') => {
        if (!textareaRef.current) return;

        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const text = content;
        const before = text.substring(0, start);
        const selection = text.substring(start, end);
        const after = text.substring(end);

        const newContent = before + prefix + (selection || 'text') + suffix + after;
        setContent(newContent);

        // Restore focus and selection
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(
                    start + prefix.length,
                    end + prefix.length + (selection ? 0 : 4) // Adjust for 'text' placeholder
                );
            }
        }, 0);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File tối đa 10MB');
            return;
        }

        try {
            const token = getToken();
            if (!token) return;

            toast.loading('Đang tải ảnh lên...', { id: 'upload-img' });
            const res = await ForumAPI.uploadImage(file, token);
            setImageUrl(res.url);
            toast.success('Đã tải ảnh lên', { id: 'upload-img' });
        } catch (error) {
            toast.error('Lỗi tải ảnh', { id: 'upload-img' });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title.trim() || !content.trim()) {
            toast.error('Vui lòng điền đầy đủ thông tin');
            return;
        }

        try {
            setSubmitting(true);
            const token = getToken();
            if (!token) {
                navigate('/login');
                return;
            }

            await ForumAPI.create({ title, content, category: categoryName, image: imageUrl || undefined }, token);
            toast.success('Bài viết đã được gửi và đang chờ duyệt!');
            navigate('/forum');
        } catch (error: any) {
            console.error('Create news error:', error);
            toast.error(error.message || 'Không thể tạo bài viết');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>
                <div className="max-w-screen-2xl mx-auto p-8 sm:p-12">
                    <div className="relative z-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-4 tracking-tight">Tạo bài viết mới</h1>
                        <p className="text-base font-mono sm:text-lg text-blue-100 dark:text-blue-200 max-w-2xl leading-relaxed">Chia sẻ suy nghĩ với lớp học</p>
                    </div>
                </div>
            </div>
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-fadeIn">
                {/* 2-Column Layout: Form + Preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* LEFT: Form Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slideUpIn">
                        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
                            {/* Image Upload */}
                            <div className="space-y-3">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                    Ảnh bìa (Tùy chọn)
                                </label>
                                {!imageUrl ? (
                                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-all relative cursor-pointer group">
                                        <input
                                            type="file"
                                            onChange={handleImageUpload}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        />
                                        <div className="text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors">
                                            <svg className="w-10 h-10 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                            <p className="font-semibold text-sm">Nhấn để tải ảnh bìa</p>
                                            <p className="text-xs mt-1 opacity-75">Hỗ trợ mọi định dạng (Max 10MB)</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-video max-h-[200px] group shadow-inner">
                                        <img
                                            src={imageUrl.startsWith('http') ? imageUrl : `${previewBaseUrl}${imageUrl}`}
                                            alt="Cover"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                // Fallback if path is weird
                                                console.error('Image load error', e);
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={() => setImageUrl('')}
                                                className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg backdrop-blur-md transition-all font-medium border border-white/20 text-sm">
                                                Xóa ảnh
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Context Info */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-xl flex items-center gap-3 border border-blue-100 dark:border-blue-800">
                                <span className="font-bold text-blue-600 dark:text-blue-400 text-sm uppercase tracking-wide">
                                    Đang đăng bài vào:
                                </span>
                                <span className="font-bold text-gray-900 dark:text-white">
                                    {categoryLabel || categoryName || '...'}
                                </span>
                            </div>

                            {/* Title */}
                            <div className="space-y-3">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                    Tiêu đề bài viết
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Nhập tiêu đề thật ấn tượng..."
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:bg-white dark:focus:bg-gray-900 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 transition-all outline-none font-semibold text-gray-800 dark:text-white placeholder-gray-400"
                                    required
                                />
                            </div>

                            {/* Content */}
                            <div className="space-y-3">
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                                    Nội dung chi tiết
                                </label>
                                <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded-t-xl border-b-0">
                                    <button type="button" onClick={() => insertMarkdown('**', '**')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="In đậm">
                                        <FaBold className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => insertMarkdown('*', '*')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="In nghiêng">
                                        <FaItalic className="w-3.5 h-3.5" />
                                    </button>
                                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                                    <button type="button" onClick={() => insertMarkdown('### ')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Tiêu đề">
                                        <FaHeading className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => insertMarkdown('> ')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Trích dẫn">
                                        <FaQuoteRight className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => insertMarkdown('```\n', '\n```')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Code block">
                                        <FaCode className="w-3.5 h-3.5" />
                                    </button>
                                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                                    <button type="button" onClick={() => insertMarkdown('- ')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Danh sách">
                                        <FaListUl className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => insertMarkdown('1. ')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Danh sách số">
                                        <FaListOl className="w-3.5 h-3.5" />
                                    </button>
                                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                                    <button type="button" onClick={() => insertMarkdown('[', '](url)')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Chèn Link">
                                        <FaLink className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => insertMarkdown('![', '](url)')} className="p-2 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors" title="Chèn Ảnh">
                                        <FaImage className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="relative">
                                    <textarea
                                        ref={textareaRef}
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        placeholder="Viết nội dung bài viết ở đây..."
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-t-0 border-gray-200 dark:border-gray-600 rounded-b-xl focus:bg-white dark:focus:bg-gray-900 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900/30 focus:border-blue-500 transition-all outline-none resize-y min-h-[300px] text-gray-800 dark:text-gray-100 leading-relaxed custom-scrollbar font-mono text-sm"
                                        required
                                    />
                                </div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">
                                    * Sử dụng Markdown để trình bày đẹp hơn
                                </p>

                                {/* Markdown Cheatsheet */}
                                <details className="group">
                                    <summary className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors select-none">
                                        <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                                        Hướng dẫn viết Markdown nhanh
                                    </summary>
                                    <div className="mt-2 bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 border border-gray-200 dark:border-gray-700 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
                                        <div className="flex items-center gap-2"><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">**đậm**</code> <span>In đậm</span></div>
                                        <div className="flex items-center gap-2"><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">*nghiêng*</code> <span>In nghiêng</span></div>
                                        <div className="flex items-center gap-2"><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded"># Tiêu đề</code> <span>Tiêu đề lớn</span></div>
                                        <div className="flex items-center gap-2"><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">- Mục</code> <span>Danh sách</span></div>
                                        <div className="flex items-center gap-2"><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">[Link](url)</code> <span>Liên kết</span></div>
                                        <div className="flex items-center gap-2"><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">&gt; Trích</code> <span>Trích dẫn</span></div>
                                    </div>
                                </details>
                            </div>

                            {/* Actions */}
                            <div className="pt-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row gap-4">
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 font-bold rounded-xl transition-all text-base"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
                                >
                                    {submitting ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Đang đăng...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                                            Đăng bài viết
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* RIGHT: Preview Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slideUpIn sticky top-8 self-start">
                        <div className="p-6 sm:p-8 space-y-4">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-3">
                                Xem trước
                            </h2>
                            {imageUrl && (
                                <div className="rounded-xl overflow-hidden shadow-sm aspect-video">
                                    <img
                                        src={imageUrl.startsWith('http') ? imageUrl : `${previewBaseUrl}${imageUrl}`}
                                        alt="Cover Preview"
                                        className="w-full h-full object-cover"
                                        onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                                    />
                                </div>
                            )}
                            {title && (
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                                    {title}
                                </h3>
                            )}
                            {content ? (
                                <div className="prose dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-pre:bg-gray-900 prose-pre:text-gray-100">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                                        {content}
                                    </ReactMarkdown>
                                </div>
                            ) : (
                                <p className="text-gray-400 dark:text-gray-500 italic text-center py-8">
                                    Nội dung sẽ hiển thị ở đây...
                                </p>
                            )}
                        </div>
                    </div>
                </div >
            </div >
        </>
    );
};

export default CreateForumPostPage;
